<script lang="ts">
	import { onDestroy } from 'svelte';
	import { Button, Container, IconButton, PlainTable, Text } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import {
		buildDeviceTableColumns,
		DEVICE_TABLE_NAME_COLUMN_ID,
		movableDeviceTableColumnIds,
		parseSavedDeviceTableColumns,
		shiftDeviceTableColumn,
		type DeviceTableColumn,
	} from 'src/helpers/device_table_layout_helpers';
	import { saveDeviceListSettings } from 'src/helpers/home_helpers';
	import { home_data } from 'src/stores';
	import type { Device } from 'src/types/Device';
	import DeviceTableRow from './DeviceTableRow.svelte';

	export let items: Device[] = [];

	let columnEditMode = false;
	let localColumnOrder: string[] | null = null;
	let persistTimer: ReturnType<typeof setTimeout> | undefined;

	$: savedColumns = parseSavedDeviceTableColumns($home_data?.device_list?.columns);
	$: columns = buildDeviceTableColumns(items, localColumnOrder ?? savedColumns);

	const headerLongpress = (node: HTMLElement) => {
		const threshold = 500;
		const onPointerDown = () => {
			const timeout = setTimeout(() => {
				node.dispatchEvent(new CustomEvent('longpress'));
			}, threshold);
			const cancel = () => {
				clearTimeout(timeout);
				node.removeEventListener('mouseup', cancel);
				node.removeEventListener('mouseleave', cancel);
				node.removeEventListener('touchend', cancel);
				node.removeEventListener('touchcancel', cancel);
			};
			node.addEventListener('mouseup', cancel, { passive: true });
			node.addEventListener('mouseleave', cancel, { passive: true });
			node.addEventListener('touchend', cancel, { passive: true });
			node.addEventListener('touchcancel', cancel, { passive: true });
		};

		node.addEventListener('mousedown', onPointerDown, { passive: true });
		node.addEventListener('touchstart', onPointerDown, { passive: true });

		return {
			destroy() {
				node.removeEventListener('mousedown', onPointerDown);
				node.removeEventListener('touchstart', onPointerDown);
			},
		};
	};

	const headerLabel = (column: DeviceTableColumn) => {
		if (column.kind === 'name') return $format('label_name', { default: 'Name' });
		if (column.kind === 'state') return $format('label_state', { default: 'State' });
		if (column.kind === 'room') return $format('label_room', { default: 'Room' });
		if (column.kind === 'appliance') return $format('label_appliance', { default: 'Appliance' });
		if (column.kind === 'type') return $format('label_type', { default: 'Type' });
		return column.label;
	};

	const headerClass = (column: DeviceTableColumn) => {
		if (column.kind === 'name') return 'text-left device-table-col-name';
		if (column.kind === 'state') return 'text-center device-table-col-state';
		if (column.kind === 'room') return 'text-left device-table-col-room';
		if (column.kind === 'appliance') return 'text-left device-table-col-appliance';
		if (column.kind === 'type') return 'text-left device-table-col-type';
		return 'text-left device-table-col-status';
	};

	const persistColumns = (ids: string[]) => {
		clearTimeout(persistTimer);
		persistTimer = setTimeout(() => {
			void saveDeviceListSettings({ columns: ids });
		}, 400);
	};

	const flushPersist = () => {
		clearTimeout(persistTimer);
		if (!localColumnOrder) return;
		void saveDeviceListSettings({ columns: localColumnOrder });
	};

	const enterColumnEdit = () => {
		columnEditMode = true;
	};

	const exitColumnEdit = () => {
		columnEditMode = false;
		flushPersist();
	};

	onDestroy(() => {
		flushPersist();
	});

	const onWindowKeydown = (event: KeyboardEvent) => {
		if (event.key === 'Escape' && columnEditMode) {
			exitColumnEdit();
		}
	};

	const shiftColumn = (columnId: string, direction: -1 | 1) => {
		const next = shiftDeviceTableColumn(movableDeviceTableColumnIds(columns), columnId, direction);
		localColumnOrder = next;
		persistColumns(next);
	};
</script>

<svelte:window on:keydown={onWindowKeydown} />

<Container type="glass" padding="s" noScroll class="device-table-surface">
	{#if columnEditMode}
		<div class="device-table-edit-bar">
			<Text styleType="body-extra-small" class="text-color-muted">
				{$format('label_rearrange_table_columns', {
					default: 'Use the arrows to rearrange columns.',
				})}
			</Text>
			<Button size="s" variant="primary" on:click={exitColumnEdit}>
				{$format('label_done', { default: 'Done' })}
			</Button>
		</div>
	{/if}
	<div class="device-table-scroll">
		<PlainTable type="compact" class="device-table">
			<thead
				class:is-editing={columnEditMode}
				title={$format('label_long_press_rearrange_columns', {
					default: 'Long-press to rearrange columns',
				})}
				use:headerLongpress
				on:longpress={enterColumnEdit}
				on:contextmenu|preventDefault
			>
				<tr>
					{#each columns as column, index (column.id)}
						<th class={headerClass(column)}>
							{#if columnEditMode && column.id !== DEVICE_TABLE_NAME_COLUMN_ID}
								<div class="device-table-header-edit">
									<IconButton
										variant="muted"
										icon="fas fa-chevron-left"
										disabled={index <= 1}
										aria-label={$format('label_move_column_left', {
											default: 'Move column left',
										})}
										on:click={() => shiftColumn(column.id, -1)}
									/>
									<span>{headerLabel(column)}</span>
									<IconButton
										variant="muted"
										icon="fas fa-chevron-right"
										disabled={index >= columns.length - 1}
										aria-label={$format('label_move_column_right', {
											default: 'Move column right',
										})}
										on:click={() => shiftColumn(column.id, 1)}
									/>
								</div>
							{:else}
								{headerLabel(column)}
							{/if}
						</th>
					{/each}
				</tr>
			</thead>
			<tbody>
				{#each items as device (device.id)}
					<DeviceTableRow {device} {columns} />
				{/each}
			</tbody>
		</PlainTable>
	</div>
</Container>

<style>
	.device-table-scroll {
		width: 100%;
		overflow-x: auto;
	}

	.device-table-edit-bar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		margin-bottom: 8px;
	}

	.device-table-header-edit {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		white-space: nowrap;
	}

	:global(.device-table-surface .device-table) {
		width: 100%;
		table-layout: auto;
	}

	:global(.device-table thead) {
		user-select: none;
		-webkit-user-select: none;
	}

	:global(.device-table thead.is-editing) {
		background-color: var(--color-1-opacity);
	}

	/* Shrink identity columns to content so status columns can share leftover space. */
	:global(.device-table-col-name),
	:global(.device-table-col-room),
	:global(.device-table-col-appliance),
	:global(.device-table-col-type),
	:global(.device-table-col-state) {
		width: 1%;
		white-space: nowrap;
	}

	:global(.device-table-col-name) {
		min-width: 132px;
		max-width: 176px;
	}

	:global(.device-table-col-room),
	:global(.device-table-col-appliance),
	:global(.device-table-col-type) {
		min-width: 64px;
		max-width: 112px;
	}

	:global(.device-table-col-state) {
		min-width: 48px;
	}

	:global(.device-table-col-status) {
		min-width: 108px;
		padding-left: 12px;
		padding-right: 12px;
	}

	:global(.device-table tbody tr) {
		cursor: pointer;
	}

	:global(.device-table tbody tr:hover) {
		background-color: var(--color-0);
	}

	:global(.device-table tbody tr.is-selected) {
		background-color: var(--color-1-opacity);
	}

	:global(.device-table tbody tr.is-offline) {
		opacity: 0.65;
	}

	:global(.device-table .device-table-name-inner) {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
	}

	:global(.device-table .device-table-name-text) {
		min-width: 0;
		max-width: 140px;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	:global(.device-table .device-table-state) {
		vertical-align: middle;
	}

	:global(.device-table-header-edit .icon-button) {
		width: 24px;
		height: 24px;
		min-width: 24px;
		padding: 0;
	}
</style>
